import { priorityLabel } from '../../utils/presentation';
import { endOfDueDay } from '../../utils/taskPresentation';
import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import './AddAssetModal.css';
import { tasksService, facilitiesService, clientsService } from '../../services';
import { useToast } from '../../contexts/ToastContext';
import { assigneeLabel } from '../../utils/taskPresentation';
import { Task, User, Facility, Client } from '../../types';

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (task: Task) => void;
  facilityId?: string;
  createdById: string;
}

interface TaskFormData {
  description: string;
  responsible_user_id: string;
  priority: string;
  category: string;
  due_by: string;
}

const DEFAULT_FORM: TaskFormData = {
  description: '',
  responsible_user_id: '',
  priority: 'medium',
  category: '',
  due_by: '',
};

const AddTaskModal: React.FC<AddTaskModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  facilityId,
  createdById,
}) => {
  const [formData, setFormData] = useState<TaskFormData>(DEFAULT_FORM);
  const [users, setUsers] = useState<User[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedFacilityId, setSelectedFacilityId] = useState(facilityId || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { showSuccess } = useToast();

  useEffect(() => {
    let cancelled = false;
    setUsers([]);
    setFormData(prev => ({ ...prev, responsible_user_id: '' }));
    if (!isOpen || !selectedFacilityId) return;
    setIsLoading(true);
    facilitiesService.getAssignableUsers(selectedFacilityId).then(data => {
      if (cancelled) return;
      setUsers(data);
      const creator = data.find(user => user.id === createdById);
      setFormData(prev => ({ ...prev, responsible_user_id: creator?.id || '' }));
    }).catch(() => {
      if (!cancelled) setErrors(prev => ({ ...prev, submit: 'Could not load assignees. Please try again.' }));
    }).finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, selectedFacilityId, createdById]);

  useEffect(() => {
    setErrors(previous => {
      const next = { ...previous };
      if (formData.description.trim()) delete next.description;
      if (formData.responsible_user_id) delete next.responsible_user_id;
      if (selectedClientId) delete next.client;
      if (selectedFacilityId) delete next.facility;
      return next;
    });
  }, [formData.description, formData.responsible_user_id, selectedClientId, selectedFacilityId]);

  const loadFacilitiesAndClients = useCallback(async () => {
    try {
      const [facilityData, clientData] = await Promise.all([
        facilitiesService.getFacilities(),
        clientsService.getClients(),
      ]);
      setFacilities(facilityData);
      setClients(clientData);
    } catch (error) {
      console.error('Failed to load facilities or clients', error);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setFormData(DEFAULT_FORM);
    setErrors({});
    setSelectedClientId('');
    setSelectedFacilityId(facilityId || '');
    if (!facilityId) {
      loadFacilitiesAndClients();
    }
  }, [facilityId, isOpen, loadFacilitiesAndClients]);

  const availableFacilities = selectedClientId
    ? facilities.filter((facility) => facility.client_id === selectedClientId)
    : [];

  const availableUsers = users;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.responsible_user_id) {
      newErrors.responsible_user_id = 'Assignee is required';
    }

    if (!facilityId && !selectedClientId) {
      newErrors.client = 'Client is required';
    }

    if (!selectedFacilityId) {
      newErrors.facility = 'Facility is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const newTask = await tasksService.createTask({
        description: formData.description.trim(),
        created_by_id: createdById,
        responsible_user_id: formData.responsible_user_id,
        priority: formData.priority,
        category: formData.category.trim() || undefined,
        facility_id: selectedFacilityId,
        asset_id: undefined, // Explicitly null for facility-level tasks
        due_by: formData.due_by ? endOfDueDay(formData.due_by) : undefined,
      });

      showSuccess('Action created');
      onSuccess(newTask);
      onClose();
    } catch (error) {
      console.error('Failed to create task', error);
      setErrors({ submit: 'Failed to create task. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Facility-Level Action</h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {!facilityId && (
            <>
              <div className="form-group">
                <label htmlFor="client">Client *</label>
                <select
                  id="client"
                  value={selectedClientId}
                  onChange={(e) => {
                    setSelectedClientId(e.target.value);
                    setSelectedFacilityId('');
                    setFormData((prev) => ({ ...prev, responsible_user_id: '' }));
                  }}
                  className={errors.client ? 'error' : ''}
                >
                  <option value="">Select a client...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
                {errors.client && (
                  <span className="error-message">{errors.client}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="facility">Facility *</label>
                <select
                  id="facility"
                  value={selectedFacilityId}
                  onChange={(e) => {
                    const nextFacilityId = e.target.value;
                    setSelectedFacilityId(nextFacilityId);
                    const nextFacility = facilities.find((facility) => facility.id === nextFacilityId);
                    setFormData((prev) => ({
                      ...prev,
                      responsible_user_id:
                        nextFacility && prev.responsible_user_id
                          ? prev.responsible_user_id
                          : '',
                    }));
                  }}
                  className={errors.facility ? 'error' : ''}
                  disabled={!selectedClientId}
                >
                  <option value="">
                    {selectedClientId ? 'Select a facility...' : 'Select a client first'}
                  </option>
                  {availableFacilities.map((facility) => (
                    <option key={facility.id} value={facility.id}>{facility.name}</option>
                  ))}
                </select>
                {errors.facility && (
                  <span className="error-message">{errors.facility}</span>
                )}
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the action or task..."
              rows={3}
              className={errors.description ? 'error' : ''}
            />
            {errors.description && (
              <span className="error-message">{errors.description}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="responsible_user_id">Assign To *</label>
            <select
              id="responsible_user_id"
              value={formData.responsible_user_id}
              onChange={(e) => setFormData({ ...formData, responsible_user_id: e.target.value })}
              className={errors.responsible_user_id ? 'error' : ''}
              disabled={isLoading || (!facilityId && !selectedFacilityId)}
            >
              <option value="">
                {facilityId || selectedFacilityId ? 'Select a person...' : 'Select a facility first'}
              </option>
              {availableUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {assigneeLabel(user)}
                </option>
              ))}
            </select>
            {errors.responsible_user_id && (
              <span className="error-message">{errors.responsible_user_id}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <option value="low">{priorityLabel('low')}</option>
                <option value="medium">{priorityLabel('medium')}</option>
                <option value="high">{priorityLabel('high')}</option>
                <option value="critical">{priorityLabel('critical')}</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="due_by">Due Date</label>
              <input
                type="date"
                id="due_by"
                value={formData.due_by}
                onChange={(e) => setFormData({ ...formData, due_by: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="category">Category (Optional)</label>
            <input
              type="text"
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              placeholder="e.g., Maintenance, Compliance, Safety..."
            />
          </div>

          {errors.submit && (
            <div className="error-message" style={{ marginTop: '1rem' }}>
              {errors.submit}
            </div>
          )}
        </form>

        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? 'Creating...' : 'Create Action'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTaskModal;
